import {
  cloneElement,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

export type TooltipSide = 'top' | 'bottom';
export type TooltipAlign = 'start' | 'center' | 'end';
const TOOLTIP_CLOSE_DELAY_MS = 220;

interface RectLike {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
}

interface SizeLike {
  width: number;
  height: number;
}

interface TooltipPosition {
  top: number;
  left: number;
  arrowLeft: number;
  side: TooltipSide;
}

export function getTooltipPosition({
  trigger,
  tooltip,
  viewport,
  preferredSide = 'top',
  align = 'center',
  gap = 9,
  margin = 10,
}: {
  trigger: RectLike;
  tooltip: SizeLike;
  viewport: SizeLike;
  preferredSide?: TooltipSide;
  align?: TooltipAlign;
  gap?: number;
  margin?: number;
}): TooltipPosition {
  const spaceAbove = trigger.top - margin;
  const spaceBelow = viewport.height - trigger.bottom - margin;
  const fitsAbove = spaceAbove >= tooltip.height + gap;
  const fitsBelow = spaceBelow >= tooltip.height + gap;
  let side = preferredSide;

  if (preferredSide === 'top' && !fitsAbove && fitsBelow) side = 'bottom';
  if (preferredSide === 'bottom' && !fitsBelow && fitsAbove) side = 'top';
  if (!fitsAbove && !fitsBelow) side = spaceAbove >= spaceBelow ? 'top' : 'bottom';

  const unclampedTop = side === 'top' ? trigger.top - tooltip.height - gap : trigger.bottom + gap;
  const maxTop = Math.max(margin, viewport.height - tooltip.height - margin);
  const top = Math.min(Math.max(unclampedTop, margin), maxTop);

  const unclampedLeft =
    align === 'start'
      ? trigger.left
      : align === 'end'
        ? trigger.right - tooltip.width
        : trigger.left + trigger.width / 2 - tooltip.width / 2;
  const maxLeft = Math.max(margin, viewport.width - tooltip.width - margin);
  const left = Math.min(Math.max(unclampedLeft, margin), maxLeft);
  const triggerCenter = trigger.left + trigger.width / 2;
  const arrowInset = Math.min(14, tooltip.width / 2);
  const arrowLeft = Math.min(
    Math.max(triggerCenter - left, arrowInset),
    Math.max(arrowInset, tooltip.width - arrowInset),
  );

  return { top, left, arrowLeft, side };
}

interface TooltipTriggerProps {
  'aria-describedby'?: string;
  ref?: Ref<HTMLElement>;
  onBlur?: (event: ReactFocusEvent<HTMLElement>) => void;
  onClick?: (event: ReactMouseEvent<HTMLElement>) => void;
  onFocus?: (event: ReactFocusEvent<HTMLElement>) => void;
  onKeyDown?: (event: ReactKeyboardEvent<HTMLElement>) => void;
  onPointerEnter?: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerLeave?: (event: ReactPointerEvent<HTMLElement>) => void;
}

function assignRef(ref: Ref<HTMLElement> | undefined, node: HTMLElement | null) {
  if (typeof ref === 'function') ref(node);
  else if (ref) (ref as MutableRefObject<HTMLElement | null>).current = node;
}

/**
 * Ayuda breve para acciones. Usa hover con puntero fino y foco de teclado;
 * nunca intercepta el primer toque en pantallas táctiles.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  delay = 320,
}: {
  content: ReactNode;
  children: ReactElement;
  side?: TooltipSide;
  align?: TooltipAlign;
  delay?: number;
}) {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const child = children as ReactElement<TooltipTriggerProps>;
  const childRef = (children as ReactElement & { ref?: Ref<HTMLElement> }).ref;
  const visible = hovered || focused;

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current === null) return;
    window.clearTimeout(openTimerRef.current);
    openTimerRef.current = null;
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current === null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const dismiss = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    setHovered(false);
    setFocused(false);
  }, [clearCloseTimer, clearOpenTimer]);

  const showForFocus = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    setFocused(true);
  }, [clearCloseTimer, clearOpenTimer]);

  const showAfterDelay = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    openTimerRef.current = window.setTimeout(() => setHovered(true), delay);
  }, [clearCloseTimer, clearOpenTimer, delay]);

  const keepOpenForPointer = useCallback(() => {
    clearCloseTimer();
    setHovered(true);
  }, [clearCloseTimer]);

  const hideAfterPointerLeaves = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setHovered(false), TOOLTIP_CLOSE_DELAY_MS);
  }, [clearCloseTimer, clearOpenTimer]);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) return;
    setPosition(
      getTooltipPosition({
        trigger: trigger.getBoundingClientRect(),
        tooltip: tooltip.getBoundingClientRect(),
        viewport: { width: window.innerWidth, height: window.innerHeight },
        preferredSide: side,
        align,
      }),
    );
  }, [align, side]);

  useEffect(() => {
    if (!visible) {
      setPosition(null);
      return undefined;
    }
    updatePosition();
    const frame = window.requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', dismissOnEscape);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      document.removeEventListener('keydown', dismissOnEscape);
    };
  }, [dismiss, updatePosition, visible]);

  useEffect(
    () => () => {
      clearOpenTimer();
      clearCloseTimer();
    },
    [clearCloseTimer, clearOpenTimer],
  );

  const describedBy = [child.props['aria-describedby'], tooltipId].filter(Boolean).join(' ');
  const trigger = cloneElement(child, {
    'aria-describedby': describedBy,
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      assignRef(childRef, node);
    },
    onPointerEnter: (event: ReactPointerEvent<HTMLElement>) => {
      child.props.onPointerEnter?.(event);
      if (event.defaultPrevented || event.pointerType === 'touch') return;
      if (!window.matchMedia('(any-hover: hover) and (any-pointer: fine)').matches) return;
      showAfterDelay();
    },
    onPointerLeave: (event: ReactPointerEvent<HTMLElement>) => {
      child.props.onPointerLeave?.(event);
      hideAfterPointerLeaves();
    },
    onFocus: (event: ReactFocusEvent<HTMLElement>) => {
      child.props.onFocus?.(event);
      if (event.currentTarget.matches(':focus-visible')) showForFocus();
    },
    onBlur: (event: ReactFocusEvent<HTMLElement>) => {
      child.props.onBlur?.(event);
      setFocused(false);
    },
    onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
      child.props.onKeyDown?.(event);
      if (event.key === 'Escape') dismiss();
    },
    onClick: (event: ReactMouseEvent<HTMLElement>) => {
      child.props.onClick?.(event);
      dismiss();
    },
  });

  const bubble = (
    <div
      ref={tooltipRef}
      id={tooltipId}
      role="tooltip"
      onPointerEnter={keepOpenForPointer}
      onPointerLeave={hideAfterPointerLeaves}
      data-state={visible && position ? 'open' : 'closed'}
      data-side={position?.side ?? side}
      className={`ui-tooltip fixed z-[90] w-max max-w-[min(280px,calc(100vw-20px))] break-words rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-left text-xs font-medium leading-4 text-white shadow-[0_14px_34px_-12px_rgba(15,23,42,.65)] [overflow-wrap:anywhere] transition-opacity duration-150 ${visible && position ? 'pointer-events-auto visible opacity-100' : 'pointer-events-none invisible opacity-0'}`}
      style={{
        top: position?.top ?? -10_000,
        left: position?.left ?? -10_000,
      }}
    >
      {content}
      {position && (
        <span
          aria-hidden
          className={`absolute h-2 w-2 -translate-x-1/2 rotate-45 border-slate-700 bg-slate-900 ${position.side === 'top' ? '-bottom-1 border-b border-r' : '-top-1 border-l border-t'}`}
          style={{ left: position.arrowLeft }}
        />
      )}
    </div>
  );

  return (
    <>
      {trigger}
      {typeof document === 'undefined'
        ? cloneElement(bubble, { className: 'sr-only' })
        : createPortal(bubble, document.body)}
    </>
  );
}
